// Search results catalog: turns a `configs_result_fields` column descriptor
// (`{ fieldKey, fieldType, tabKey, tabOrder, fieldOrder, sortable, valueSchema }`, as returned by
// `POST /{assetClass}/search/results`) into a bootstrap-vue `b-table` field definition, so the
// dynamic grid can reuse `ResultsTable.vue` unmodified — same "BE tells the shape, FE renders
// generically" pattern already used by the filter catalog's `FilterField.vue`.
//
// Enum-typed columns project as their .NET enum name (not the numeric value the legacy `/search`
// page's fixed columns used) — see `ResultFieldProjectorRegistry` on the BE — so they reuse the
// SAME i18n option maps (`search.options.<EnumName>.<value>`) already populated for the filter
// catalog's MultiSelect options, instead of the older numeric-keyed `search.filterSelectValues.*`
// maps that only cover the four legacy IBIPs enums.

// Result-field keys whose catalog `values_source` enum name differs from the field_key itself
// (Product property name convention) — everything else maps 1:1 (e.g. AcepiCategory -> AcepiCategory).
const ENUM_OPTIONS_KEY_ALIAS = {
  Structure: 'StructureTypes',
  Type: 'ProductTypes',
  PremiumType: 'PremiumTypes',
}

// Field keys whose legacy `search.resultTable.*` label lives under a different literal key than
// `lowerFirst(fieldKey)` — every other field_key (Structure, Sri, SriMin, FirstRevisionDate,
// the new IC fields, ...) matches its lowerFirst label key directly.
const LABEL_KEY_OVERRIDES = {
  IssuerName: 'issuer',
  PremiumType: 'premium',
  DistributionChannels: 'channel',
  TotalSids: 'investmentOptions',
  // ReferenceDate only ever appears in the "Revisions" tab in this catalog (never as the generic
  // "revision date" meaning of the unrelated existing `resultTable.referenceDate` key) — always
  // "Ultima revisione", matching the legacy revisionsFields label.
  ReferenceDate: 'lastRevision',
  // RhpMax (General) and RhpMin (Indicators, MOP-only) are genuinely different values (max/min of
  // Product.RhpTo/RhpFrom — see MinMaxFiguresChecker), but the legacy grid deliberately labels both
  // just "RHP" (generalFields/mopFields in Search.vue), never "(Max)"/"(Min)" — explicit choice to
  // match legacy's label exactly rather than the more precise SriMin/SriMax-style naming.
  RhpMax: 'rhp',
  RhpMin: 'rhp',
}

// Tab labels — mirrors `search.tabHeader.*`. `Payout` only ever appears for Investment
// Certificates (see FILTER_CATALOG_DESIGN.md) — IBIPs columns never carry it.
// `tab_key` values are English identifiers (DB-side rename, Search page only); the displayed
// label stays whatever `search.tabHeader.*` resolves to (Italian today).
const TAB_LABEL_KEYS = {
  General: 'search.tabHeader.general',
  Indicators: 'search.tabHeader.indicators',
  Payout: 'search.tabHeader.payout',
  Revisions: 'search.tabHeader.revisions',
}

export function tabLabel(ctx, tabKey) {
  const key = TAB_LABEL_KEYS[tabKey]
  return key && ctx.$te(key) ? ctx.$t(key) : tabKey
}

// Derives the tabs to render from the columns actually returned by the BE for the current asset
// class, instead of a hardcoded list — IBIPs has General/Indicators/Revisions, Investment
// Certificates additionally has Payout. Order follows `tabOrder` from the catalog. Only ever
// called on /search-new's columns (page_key=Search) — the Followed page has no tab switcher.
export function availableTabs(columns) {
  const byTabKey = new Map()
  columns.forEach((column) => {
    if (!byTabKey.has(column.tabKey)) {
      byTabKey.set(column.tabKey, column.tabOrder)
    }
  })
  return [...byTabKey.entries()].sort((a, b) => a[1] - b[1]).map(([tabKey]) => tabKey)
}

function lowerFirst(text) {
  return text.charAt(0).toLowerCase() + text.slice(1)
}

export function resultFieldKey(fieldKey) {
  return lowerFirst(fieldKey)
}

export function resultFieldLabel(ctx, fieldKey) {
  const suffix = LABEL_KEY_OVERRIDES[fieldKey] || lowerFirst(fieldKey)
  const key = 'search.resultTable.' + suffix
  return ctx.$te(key) ? ctx.$t(key) : fieldKey
}

function resultFieldEnumLabel(ctx, fieldKey, code) {
  if (code === null || code === undefined) {
    return ctx.$t('common.notAvailable')
  }
  const optionsKey = ENUM_OPTIONS_KEY_ALIAS[fieldKey] || fieldKey
  const key = 'search.options.' + optionsKey + '.' + code
  return ctx.$te(key) ? ctx.$t(key) : code
}

function buildFormatter(ctx, column) {
  switch (column.fieldType) {
    case 'Percent':
      return (value) => (value != null ? ctx.$n(value, 'percent') : ctx.$t('common.notAvailable'))
    case 'Date':
      return (value) => (value ? ctx.$d(new Date(value), 'short') : '')
    case 'Enum':
      return (value) => resultFieldEnumLabel(ctx, column.fieldKey, value)
    case 'EnumList':
      return (value) => (Array.isArray(value) && value.length > 0 ? value.map((v) => resultFieldEnumLabel(ctx, column.fieldKey, v)).join(', ') : ctx.$t('common.notAvailable'))
    case 'Boolean':
      return (value) => (value == null ? ctx.$t('common.notAvailable') : ctx.$t(value ? 'common.yes' : 'common.no'))
    default:
      return (value) => (value != null && value !== '' ? value : ctx.$t('common.notAvailable'))
  }
}

// Legacy alignment convention (Search.vue's generalFields/sopFields/mopFields/revisionsFields):
// text columns (issuerName, name) are left-aligned (no class at all — the table's own default);
// every other column is centered. `generalFields`/`revisionsFields` additionally force a
// single-line header (text-nowrap), but `sopFields`/`mopFields` (Indicatori/Indicators tab)
// deliberately don't — SRI/RIY/Costs/AIE/RHP labels are long enough that legacy lets the header
// wrap onto multiple lines there instead of forcing an overflow.
function buildAlignmentClass(column) {
  if (column.fieldType === 'String') {
    return []
  }
  return column.tabKey === 'Indicators' ? ['text-center'] : ['text-nowrap', 'text-center']
}

// Builds the bootstrap-vue `fields` array for one tab. `columns` is the full, already SOP/MOP-
// resolved list returned by the BE (see FILTER_CATALOG_DESIGN.md's variant resolution algorithm —
// the FE does not need to know about SOP/MOP at all: a set with a MOP already comes back with
// `SriMin`/`SriMax` instead of `Sri`, etc). `tabKey` is optional: /search-new passes the currently
// selected tab to filter a page_key=Search response down to it; the Followed page omits it — its
// response (page_key=Followed) already contains exactly the intended columns, no filtering needed.
export function buildShownFields(ctx, columns, tabKey) {
  const scoped = tabKey ? columns.filter((column) => column.tabKey === tabKey) : columns
  return scoped
    .sort((a, b) => a.fieldOrder - b.fieldOrder)
    .map((column) => ({
      key: resultFieldKey(column.fieldKey),
      label: resultFieldLabel(ctx, column.fieldKey),
      sortable: column.sortable,
      formatter: buildFormatter(ctx, column),
      class: buildAlignmentClass(column),
      thAttr: { 'data-test': `result-field-${column.fieldKey}` },
      tdAttr: { 'data-test': `result-field-${column.fieldKey}` },
    }))
}

// Flattens one BE row (`{ id, fields: [{ key, value }] }`) into the plain object shape `b-table`
// (and `ResultsTable.vue`) expects (`{ id, issuerName: ..., name: ..., ... }`). A field_key repeated
// across tabs (e.g. IssuerName/Name) only ever needs one entry here regardless of how many tabs
// reference it.
export function flattenResultRow(row) {
  const flat = { id: row.id }
  row.fields.forEach((field) => {
    flat[resultFieldKey(field.key)] = field.value
  })
  return flat
}
